import React, { useEffect, useState } from 'react';

import { BaseControlProps } from '../../models/BaseControlProps';

import { ReactComponent as SvgLogo } from "../../components/Header/logo.svg";

import "./Register.css";
import { AuthUserRegistrationRequest } from '../../models/AuthUserRegistrationRequest';
import { AuthAPIService } from '../../services/AuthAPIServices';
import { useNavigate } from 'react-router-dom';
import { UtilService } from '../../services/UtilService';
import MuiCard from '@mui/material/Card';
import { styled } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import { Box, Button, CircularProgress, FormControl, FormHelperText, FormLabel, TextField, Typography } from '@mui/material';

const Card = styled(MuiCard)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'center',
    width: '100%',
    padding: theme.spacing(4),
    gap: theme.spacing(2),
    margin: 'auto',
    boxShadow:
      'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
    [theme.breakpoints.up('sm')]: {
      width: '450px',
    },
    ...theme.applyStyles('dark', {
      boxShadow:
        'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
    }),
  }));
  
  const RegisterContainer = styled(Stack)(({ theme }) => ({
    padding: 20,
    marginTop: '10vh',
    '&::before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      zIndex: -1,
      inset: 0
    },
  }));

const Register = (props: BaseControlProps) => {
    const [peopleXDId, setPeopleXDId] = useState<string | null>(localStorage.getItem('peopleXDId') || null);
    const [scope, apply] = React.useState<AuthUserRegistrationRequest>({
        userName: "",
        email: "",
        password: "",
        encryptedPeopleXDId: peopleXDId,
    });
    const authService = new AuthAPIService();
    const navigate = useNavigate();
    const [emailError, setEmailError] = React.useState(false);
    const [emailErrorMessage, setEmailErrorMessage] = React.useState('');
    const [passwordError, setPasswordError] = React.useState(false);
    const [passwordErrorMessage, setPasswordErrorMessage] = React.useState('');
    const [confirmPasswordError, setConfirmPasswordError] = React.useState(false);
    const [confirmPasswordErrorMessage, setConfirmPasswordErrorMessage] = React.useState('');
    const [inputValid, isInputValid] = useState(false);
    const [isLoading, setIsLoading]  = useState(false); 
    
    useEffect(() => {
        
        if (peopleXDId == null) 
        {
            navigate("/validate");
        }

    }, [peopleXDId])

    function applyScope(){
        apply(UtilService.cloneObject(scope));
    }

    const registerAccount = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!inputValid) { return; }
        setIsLoading(true);
        authService.registerUser(scope).then((response)=>{
            localStorage.removeItem('peopleXDId');
            setIsLoading(false);
            if(response.error){
                if (props.isMessageInfoShow && props.setMessageInfo) {
                    props.setMessageInfo(UtilService.getErrorMessageInfo(response.error));
                    props.isMessageInfoShow(true);
                    navigate('/validate');
                }
            }
            else{
                if(response.response){
                    if (props.isMessageInfoShow && props.setMessageInfo) {
                        props.setMessageInfo(UtilService.getSucessMessageInfo("Your account has been created successfully! An email has been sent to verify your account."));
                        props.isMessageInfoShow(true);
                    }
                    navigate('/login');
                }
                else{
                    if (props.isMessageInfoShow && props.setMessageInfo) {
                        props.setMessageInfo(UtilService.getErrorMessageInfo("We are sorry, but we couldn’t register you, please contact Mercer"));
                        props.isMessageInfoShow(true);
                    }
                    navigate('/validate');
                }
            }
        })
    }

    const validateInputs = () => {
        const email = document.getElementById('email') as HTMLInputElement;
        const password = document.getElementById('password') as HTMLInputElement;
        const confirmPassword = document.getElementById('confirmPassword') as HTMLInputElement;
        const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&_#])[A-Za-z\d@$!%*?&_#]{8,}$/;
        scope.userName = email.value;
        scope.email = email.value;
        scope.password = password.value;
        applyScope();
        let isValid = true;

        if (!email.value || !/\S+@\S+\.\S+/.test(email.value)) {
          setEmailError(true);
          setEmailErrorMessage('Please enter a valid email address.');
          isValid = false;
        } else {
          setEmailError(false);
          setEmailErrorMessage('');
        }
    
        if (!password.value || password.value.length < 8) {
          setPasswordError(true);
          setPasswordErrorMessage('Password must be at least 8 characters long.');
          isValid = false;
        } else if (!passwordPattern.test(password.value)) {
          setPasswordError(true);
          setPasswordErrorMessage('Password must contain at least 1 uppercase letter, 1 lowercase letter, 1 number, and 1 special character.');
          isValid = false;
        } else {
          setPasswordError(false);
          setPasswordErrorMessage('');
        }
    
        if (!confirmPassword.value || confirmPassword.value !== password.value) {
          setConfirmPasswordError(true);
          setConfirmPasswordErrorMessage('Password and Confrim Password does not match.');
          isValid = false;
        } else {
          setConfirmPasswordError(false);
          setConfirmPasswordErrorMessage('');
        }

        if (!isValid) {
            isInputValid(false);
        } else {
            isInputValid(true);
        }
    
        return isValid;
      };

    return(
        <RegisterContainer direction="column" justifyContent="space-between">
            <Card variant='outlined'>
                <div className="logo-container">
                    <SvgLogo focusable={false} viewBox="0 0 500 150" />
                </div>
                <Typography
                    component="h1"
                    variant="h6"
                    sx={{ width: '100%', fontSize: 'clamp(2rem, 10vw, 2.15rem)' }}
                >
                    Create your account
                </Typography>
                <Box
                    component="form"
                    onSubmit={registerAccount}
                    sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                >
                    <FormControl>
                        <FormLabel htmlFor="email">Email: </FormLabel>
                        <TextField 
                            required
                            fullWidth
                            id="email"
                            placeholder="you@email.com"
                            name="email"
                            autoComplete="email"
                            variant="outlined"
                            error={emailError}
                            helperText={emailErrorMessage}
                            color={emailError ? 'error' : 'primary' }
                        />
                    </FormControl>
                    <FormControl>
                        <FormLabel htmlFor="password" id="password-label">Password: 
                        </FormLabel>
                        <TextField
                            required
                            fullWidth
                            id="password"
                            name="password"
                            placeholder="••••••••"
                            type="password"
                            variant="outlined"
                            error={passwordError}
                            helperText={passwordErrorMessage}
                            color={passwordError ? 'error' : 'primary' }
                        />
                        <FormHelperText>
                            <i>(Password must be minimum 8 characters with at least 1 uppercase letter, 1 lower case letter, 1 number, 1 special character)</i>
                        </FormHelperText>
                    </FormControl>
                    <FormControl>
                        <FormLabel id="confirmPasswordLabel" htmlFor="confirmPasswordLabel">Confirm your password:</FormLabel>
                        <TextField
                            required
                            fullWidth
                            id="confirmPassword"
                            name="confirmPassword"
                            placeholder="••••••••"
                            type="password"
                            variant="outlined"
                            error={confirmPasswordError}
                            inputProps={{
                                'aria-label': 'Re-enter your password for confirmation',
                                placeholder: '',
                            }}
                            helperText={confirmPasswordErrorMessage}
                            color={confirmPasswordError ? 'error' : 'primary' }
                        />
                    </FormControl>
                    <Button
                        disabled={isLoading}
                        type="submit"
                        fullWidth
                        variant="contained"
                        onClick={validateInputs}
                        startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
                        sx={{
                            '&.Mui-disabled': {
                                color: (theme) => theme.palette.primary.contrastText,
                            }
                        }}
                    >
                        {isLoading ? 'Loading...' : 'Register'}
                    </Button>
                </Box>
            </Card>
        </RegisterContainer>
    );
}
export default Register;
